import React from "react";
import { Link } from "react-router-dom";
import { Icon } from "@cadmakers-ui/icons";
import { DatagridRenderArg, GetColumnDefinitionsArgs } from "../Types/FeedbackDatagridInputs";
import VoteOnFeedbackButton from "../../VoteOnFeedbackButton/VoteOnFeedbackButton";
import {
  FeedbackRowActionsDropdown,
  FeedbackRowAssignedTo,
  FeedbackRowButtons,
  FeedbackRowData,
  FeedbackRowDueDate,
  FeedbackRowPriority,
  FeedbackRowStatus,
  FeedbackRowTag,
  FeedbackRowTitle,
  FeedbackRowUpvotes
} from "../Types/FeedbackRowData";
import { DateDisplay } from "../../../../../Common";
import FeedbackWorkflowStateBadge from "../../FeedbackBadges/FeedbackWorkflowStateBadge";
import FeedbackPrioritySetterContainer from "../../FeedbackPrioritySetter/FeedbackPrioritySetterContainer";
import { AuthoredByData } from "../../../../../User";
import AuthorProfileBadge from "../../../../../User/Components/Common/AuthorProfileBadge";
import { TagDisplay } from "../../../../../Tags";
import FeedbackAssignmentsDisplay from "../../FeedbackAssignment/FeedbackAssignmentsDisplay";
import FeedbackListButtons from "../FeedbackListButtons";
import FeedbackActionsDropdown from "../../FeedbackActionsDropdown/FeedbackActionsDropdown";
import { DueDateDisplay } from "../../DueDateDisplay/DueDateDisplay";

export class FeedbackListCellRenderService {
  renderArgs: GetColumnDefinitionsArgs;

  constructor(renderArgs: GetColumnDefinitionsArgs) {
    this.renderArgs = renderArgs;

    this.renderUpvotes = this.renderUpvotes.bind(this);
    this.renderTitle = this.renderTitle.bind(this);
    this.renderDate = this.renderDate.bind(this);
    this.renderStatus = this.renderStatus.bind(this);
    this.renderPriority = this.renderPriority.bind(this);
    this.renderAuthor = this.renderAuthor.bind(this);
    this.renderTags = this.renderTags.bind(this);
    this.renderAssignedTo = this.renderAssignedTo.bind(this);
    this.renderButtons = this.renderButtons.bind(this);
    this.renderActions = this.renderActions.bind(this);
    this.renderDueDate = this.renderDueDate.bind(this);
  }

  renderUpvotes({ value }: DatagridRenderArg) {
    return (
      <VoteOnFeedbackButton
        {...(value as FeedbackRowUpvotes)}
        onVote={this.renderArgs.onVoteFeedback}
      />
    );
  }

  renderTitle({ value }: DatagridRenderArg) {
    const castValue = value as FeedbackRowTitle;
    return (
      <div className={this.renderArgs.styles.feedbackTitleWrapper}>
        {this.renderArgs.isCMUser && (
          <div className={this.renderArgs.styles.feedbackIconDisplay}>
            {castValue.isPrivate && (
              <Icon
                appName="GeneralLayout"
                assetName="GENERAL_L_Lock"
                className={this.renderArgs.styles.feedbackPrivateIcon}
                title="This feedback is private and cannot be viewed by users from other companies"
              />
            )}
            {!castValue.isPrivate && (
              <Icon
                appName="GeneralLayout"
                assetName="GENERAL_L_Public_20"
                className={this.renderArgs.styles.feedbackPrivateIcon}
                title="This feedback is public and may be viewed by users from other companies"
              />
            )}
          </div>
        )}
        <div className={this.renderArgs.styles.feedbackTitleDisplay}>
          <Link to={castValue.url}>{castValue.title}</Link>
        </div>
      </div>
    );
  }

  renderDate({ value }: DatagridRenderArg) {
    return value ? <DateDisplay date={value} /> : null;
  }

  renderDueDate({ value }: DatagridRenderArg) {
    const castValue = value as FeedbackRowDueDate;
    return value ? (
      <DueDateDisplay
        dueDate={castValue.dueDate}
        enableEdit={castValue.canEdit}
        onClickEdit={castValue.onClickEdit}
      />
    ) : null;
  }

  renderStatus({ value }: DatagridRenderArg) {
    const castValue = value as FeedbackRowStatus;
    return <FeedbackWorkflowStateBadge state={castValue.state} />;
  }

  renderPriority({ value }: DatagridRenderArg) {
    const castValue = value as FeedbackRowPriority;
    return (
      <FeedbackPrioritySetterContainer
        feedbackCode={castValue.feedbackCode}
        existingPriority={castValue.priority}
        saveImmediately
        isReadonly={!castValue.canPrioritize}
      />
    );
  }

  renderAuthor({ value }: DatagridRenderArg) {
    const castValue = value as AuthoredByData;
    return castValue ? <AuthorProfileBadge authoredBy={castValue} /> : <></>;
  }

  renderTags({ value }: DatagridRenderArg) {
    const castValue = value as FeedbackRowTag[];
    return <TagDisplay selectedIds={castValue.map(v => v.id)} />;
  }

  renderAssignedTo({ value }: DatagridRenderArg) {
    const castValue = value as FeedbackRowAssignedTo;

    castValue.feedback.assignedTo = castValue.feedback.assignedTo.filter(a => a.id !== -1);
    return (
      <FeedbackAssignmentsDisplay
        feedback={castValue.feedback}
        displayVertical
        onSaveChanges={users =>
          this.renderArgs.onFeedbackAssignedUsersChange(castValue.feedback, users)
        }
      />
    );
  }

  renderButtons({ value }: DatagridRenderArg) {
    const castValue = value as FeedbackRowButtons;
    return (
      <FeedbackListButtons
        feedback={castValue.feedback}
        areTransitionsLoaded={castValue.areTransitionsLoaded}
        onStateChange={this.renderArgs.onStateChange}
        onMove={this.renderArgs.onMove}
      />
    );
  }

  renderActions({ value }: DatagridRenderArg) {
    const castValue = value as FeedbackRowActionsDropdown;
    return (
      <FeedbackActionsDropdown
        feedback={castValue.feedback}
        onStateChange={this.renderArgs.onStateChange}
        dotsMode
      />
    );
  }
}
